import PassCover from 'icons/pass-cover'
import PassLogo from 'icons/pass-logo'
import React from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { invertTextColor } from 'utils'

const isEnglish = (text) => {
  if (typeof text !== 'string') return true
  const cleanText = text.replace(/\s+/g, '').toLowerCase()
  return /[a-zA-Z]/g.test(cleanText)
}

/** contact card: pass title, name and custom contact fields */
function BusinessCardFace({ design, pass }) {
  const {
    backgroundColor,
    logoImage,
    stripImage,
    fullName,
    title,
    passCode,
    customFields = [],
  } = design

  const displayTitle =
    title || pass?.title || pass?.designation || pass?.description || 'Pass Title'

  return (
    <div
      className='tw-w-[330px] tw-max-w-full tw-shadow-[0_16px_40px_rgba(0,0,0,0.16)] tw-text-white tw-rounded-[24px] tw-overflow-hidden tw-border tw-border-white/20'
      style={{ backgroundColor }}
    >
      <div className='tw-flex tw-flex-col tw-h-full tw-justify-between tw-pb-5'>
        <div className='tw-mt-4 tw-ml-4 tw-w-[46px] tw-h-[46px] tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10'>
          {logoImage ? (
            <img
              className='tw-w-full tw-h-full tw-object-contain tw-rounded-full'
              src={logoImage}
              alt='Logo'
            />
          ) : (
            <div id='default-logo' className='tw-block'>
              <PassLogo color={invertTextColor(backgroundColor)} />
            </div>
          )}
        </div>

        <div className='tw-items-center tw-justify-center tw-overflow-hidden tw-flex tw-h-[180px] tw-my-2'>
          {stripImage ? (
            <img
              className='tw-h-full tw-w-full tw-object-cover'
              src={stripImage}
              alt='Primary strip'
            />
          ) : (
            <span
              id='default-strip'
              className='tw-flex tw-items-center tw-px-6 tw-w-full tw-aspect-[3/1]'
            >
              <div className='tw-mx-auto tw-w-2/3'>
                <PassCover color={invertTextColor(backgroundColor)} />
              </div>
            </span>
          )}
        </div>

        <div className='tw-mb-6'>
          <div className='tw-px-6'>
            <p
              className='tw-text-[11px] tw-uppercase tw-font-semibold tw-tracking-[0.2em] tw-opacity-80 tw-whitespace-nowrap'
              style={{
                color: invertTextColor(backgroundColor),
                textAlign: isEnglish(fullName) ? 'left' : 'right',
              }}
            >
              {displayTitle}
            </p>
            <p
              className='tw-text-[18px] tw-font-semibold tw-mt-1 tw-leading-tight tw-whitespace-nowrap'
              style={{
                color: invertTextColor(backgroundColor),
                textAlign: isEnglish(fullName) ? 'left' : 'right',
              }}
            >
              {fullName}
            </p>
          </div>
        </div>

        <div className='tw-flex tw-flex-col tw-items-center'>
          <div className='tw-bg-white/95 tw-mx-auto tw-rounded-[16px] tw-card-qrcode tw-max-w-[138px] tw-p-[6px] tw-shadow-[0_8px_18px_rgba(0,0,0,0.12)]'>
            <QRCodeCanvas
              value={passCode}
              title={passCode}
              viewBox='0 0 53 53'
              size={128}
            />
          </div>
          <p
            className='tw-mt-2 tw-text-[12px] tw-font-medium tw-text-center tw-opacity-90'
            style={{ color: invertTextColor(backgroundColor) }}
          >
            Save my contact details
          </p>
        </div>

        {customFields?.length > 0 ? (
          <div className='tw-mx-4 tw-mt-4 tw-rounded-[16px] tw-bg-white/10 tw-p-3 tw-backdrop-blur-sm'>
            {customFields.map((field, index) => (
              <div
                key={`${field.fieldName || 'field'}-${index}`}
                className='tw-mb-2 last:tw-mb-0'
              >
                <div className='tw-text-[10px] tw-uppercase tw-font-semibold tw-tracking-[0.2em] tw-opacity-80'>
                  {field.fieldName || 'Field'}
                </div>
                <div className='tw-text-[13px] tw-font-medium tw-break-words'>
                  {field.fieldValue || '—'}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** loyalty card: customer name over the pass QR code */
function LoyaltyFace({ design }) {
  const { backgroundColor, logoImage, stripImage, fullName, passCode } = design

  return (
    <div
      className='tw-w-[330px] tw-max-w-full tw-shadow-lg tw-text-white tw-rounded-2xl tw-overflow-hidden'
      style={{ backgroundColor }}
    >
      <div className='tw-flex tw-flex-col tw-h-full tw-justify-between tw-pb-6'>
        <div className='tw-mt-3 tw-ml-3 tw-w-1/6'>
          {logoImage ? (
            <img className='tw-w-full' src={logoImage} alt='Logo' />
          ) : (
            <div id='default-logo' className='tw-block tw-mt-3 tw-ml-3'>
              <PassLogo color={invertTextColor(backgroundColor)} />
            </div>
          )}
        </div>

        <div className='tw-items-center tw-justify-center tw-overflow-hidden tw-flex tw-aspect-[1033/407] tw-my-[15px]'>
          {stripImage ? (
            <img
              className='tw-h-full tw-w-full tw-object-cover'
              src={stripImage}
              alt='Primary strip'
            />
          ) : (
            <span
              id='default-strip'
              className='tw-flex tw-items-center tw-px-6 tw-w-full tw-aspect-[3/1]'
            >
              <div className='tw-mx-auto tw-w-2/3'>
                <PassCover color={invertTextColor(backgroundColor)} />
              </div>
            </span>
          )}
        </div>

        <div className='tw-mb-12'>
          <div className='tw-px-6'>
            <p
              className='tw-text-1xl tw-font-extralight tw-mt-[-20px]'
              style={{
                color: invertTextColor(backgroundColor),
                textAlign: isEnglish(fullName) ? 'left' : 'right',
              }}
            >
              Customer Name
            </p>
            <p
              className='tw-text-1xl tw-font-extralight'
              style={{
                color: invertTextColor(backgroundColor),
                textAlign: isEnglish(fullName) ? 'left' : 'right',
              }}
            >
              {fullName}
            </p>
          </div>
        </div>

        <div className='tw-bg-white tw-mx-auto tw-rounded-md tw-card-qrcode tw-max-w-[138px] tw-p-[5px]'>
          <QRCodeCanvas
            value={passCode}
            title={passCode}
            viewBox='0 0 53 53'
            size={128}
          />
        </div>
      </div>
    </div>
  )
}

function ApplePass({ design, pass, variant }) {
  return variant === 'business' ? (
    <BusinessCardFace design={design} pass={pass} />
  ) : (
    <LoyaltyFace design={design} />
  )
}

export default ApplePass
